import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      component: () => import('@/views/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          redirect: '/inbox',
        },
        {
          path: 'inbox',
          name: 'inbox',
          component: () => import('@/views/EmailListView.vue'),
          props: { folder: 'inbox' },
        },
        {
          path: 'sent',
          name: 'sent',
          component: () => import('@/views/EmailListView.vue'),
          props: { folder: 'sent' },
        },
        {
          path: 'drafts',
          name: 'drafts',
          component: () => import('@/views/EmailListView.vue'),
          props: { folder: 'drafts' },
        },
        {
          path: 'trash',
          name: 'trash',
          component: () => import('@/views/EmailListView.vue'),
          props: { folder: 'trash' },
        },
        {
          path: 'spam',
          name: 'spam',
          component: () => import('@/views/EmailListView.vue'),
          props: { folder: 'spam' },
        },
        {
          path: 'archive',
          name: 'archive',
          component: () => import('@/views/EmailListView.vue'),
          props: { folder: 'archive' },
        },
        {
          path: 'folder/:folderId',
          name: 'folder',
          component: () => import('@/views/EmailListView.vue'),
          props: true,
        },
        {
          path: 'email/:emailId',
          name: 'email',
          component: () => import('@/views/EmailDetailView.vue'),
          props: true,
        },
        {
          path: 'compose',
          name: 'compose',
          component: () => import('@/views/ComposeView.vue'),
        },
        {
          path: 'compose/:replyTo',
          name: 'reply',
          component: () => import('@/views/ComposeView.vue'),
          props: true,
        },
        {
          path: 'search',
          name: 'search',
          component: () => import('@/views/SearchView.vue'),
        },
        {
          path: 'contacts',
          name: 'contacts',
          component: () => import('@/views/ContactsView.vue'),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/views/SettingsView.vue'),
        },
        // Admin routes
        {
          path: 'admin',
          name: 'admin',
          component: () => import('@/views/admin/AdminDashboardView.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'admin/users',
          name: 'admin-users',
          component: () => import('@/views/admin/AdminUsersView.vue'),
          meta: { requiresAdmin: true },
        },
        {
          path: 'admin/users/:userId',
          name: 'admin-user-detail',
          component: () => import('@/views/admin/AdminUserDetailView.vue'),
          props: true,
          meta: { requiresAdmin: true },
        },
      ],
    },
    {
      path: '/offline',
      name: 'offline',
      component: () => import('@/views/OfflineView.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
    },
  ],
});

// Navigation guard for authentication
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Wait for auth check to complete
  if (!authStore.initialized) {
    await authStore.checkAuth();
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false);
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin === true);

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login
    next({ name: 'login', query: { redirect: to.fullPath } });
  } else if (requiresAdmin && !authStore.isAdmin) {
    // Non-admin trying to access admin routes
    next({ name: 'inbox' });
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    // Already logged in, redirect to inbox
    next({ name: 'inbox' });
  } else {
    next();
  }
});

export default router;
